/** @format */

import {useMemo} from 'react';

import {endOfMonth, format, startOfMonth, subDays, subMonths} from 'date-fns';
import {ru} from 'date-fns/locale';
import {useTranslation} from 'react-i18next';

import {SavingsData, UseSavingsReturn} from '../interfaces';
import {useGetStatsQuery, useGetTimeEfficiencyQuery} from '../graphql/__generated__';
import {formatTimeFromMinutes} from '../helpers/formatTimeFromMinutes';

export const useSavings = (): UseSavingsReturn => {
  const {t} = useTranslation();

  const {currentMonthStart, currentMonthEnd, previousMonth, previousMonthStart, previousMonthEnd} =
    useMemo(() => {
      const todayDate = new Date();
      const todayString = format(todayDate, 'yyyy-MM-dd');
      const normalizedToday = new Date(todayString);
      const yesterdayDate = subDays(normalizedToday, 1);

      const currentDay = normalizedToday.getDate();
      const actualCurrentMonth = currentDay === 1 ? subMonths(yesterdayDate, 0) : yesterdayDate;

      const currentMonthStartDate = startOfMonth(actualCurrentMonth);
      const currentMonthEndDate = endOfMonth(todayDate);

      const previousMonthDate = subMonths(actualCurrentMonth, 1);
      const previousMonthStartDate = startOfMonth(previousMonthDate);
      const previousMonthEndDate = endOfMonth(previousMonthDate);

      return {
        yesterday: yesterdayDate,
        currentMonthStart: currentMonthStartDate,
        currentMonthEnd: currentMonthEndDate,
        previousMonth: previousMonthDate,
        previousMonthStart: previousMonthStartDate,
        previousMonthEnd: previousMonthEndDate,
      };
    }, []);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useGetTimeEfficiencyQuery({
    variables: {
      startDate: format(currentMonthStart, 'yyyy-MM-dd'),
      endDate: format(currentMonthEnd, 'yyyy-MM-dd'),
    },
  });

  const {
    data: previousData,
    loading: previousLoading,
    error: previousError,
  } = useGetTimeEfficiencyQuery({
    variables: {
      startDate: format(previousMonthStart, 'yyyy-MM-dd'),
      endDate: format(previousMonthEnd, 'yyyy-MM-dd'),
    },
  });

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
  } = useGetStatsQuery({
    variables: {
      startDate: format(currentMonthStart, 'yyyy-MM-dd'),
      endDate: format(currentMonthEnd, 'yyyy-MM-dd'),
    },
  });

  const result = useMemo((): SavingsData | null => {
    if (
      currentLoading ||
      previousLoading ||
      statsLoading ||
      currentError ||
      previousError ||
      statsError
    ) {
      return null;
    }

    const currentEfficiency = currentData?.currentUser?.currentCompany?.timeEfficiency;
    const previousEfficiency = previousData?.currentUser?.currentCompany?.timeEfficiency;
    const stats = statsData?.currentUser?.currentCompany?.graphStats;

    const currentCost = currentEfficiency?.cost || 0;
    const currentTime = currentEfficiency?.time || 0;
    const previousCost = previousEfficiency?.cost || 0;
    const currency =
      Array.isArray(stats) && stats.length > 0
        ? stats.find(stat => stat?.currency)?.currency || '₽'
        : '₽';

    return {
      amount: currentCost,
      period: formatTimeFromMinutes(currentTime, t),
      monthAmount: previousCost > 0 ? previousCost : null,
      monthLabel: previousCost > 0 ? format(previousMonth, 'LLLL', {locale: ru}) : null,
      currency,
      status: 'default' as const,
    };
  }, [
    currentData,
    previousData,
    statsData,
    previousMonth,
    t,
    currentLoading,
    previousLoading,
    statsLoading,
    currentError,
    previousError,
    statsError,
  ]);

  return {
    data: result,
    loading: currentLoading || previousLoading || statsLoading,
    error: currentError || previousError || statsError,
  };
};
